import { Textarea } from "@component/textarea";
import { Label } from "@component/label";
import { Checkbox } from "@component/checkbox";
import {
    MessageSquare,
    User,
    MessageCircle,
    GitBranch,
    CheckCircle2,
    Circle,
} from "lucide-react";
import { InterviewAssessment } from "@type/assessments";
import { renderTextWithLineBreaks } from "@util/format";
import { defaultInterviewFlow, defaultToneAndCommunication, mapInterviewFlow, mapToneAndCommunication } from "@constant/app";

interface Props {
    mode: "view" | "edit";
    data: InterviewAssessment;
    onDataChange: (data: InterviewAssessment) => void;
    onValidationChange?: (isValid: boolean) => void;
}

export default function InterviewContextSection({
    mode,
    data,
    onDataChange,
    onValidationChange,
}: Props) {
    // Calculate progress checklist
    const checklistItems = [
        {
            label: "Set your Interviewer Persona",
            completed: !!(
                data.content.interviewerPersona && data.content.interviewerPersona.trim()
            ),
        },
        {
            label: "Select at least 1 tone for your AI interviewer",
            completed: !!(
                data.content.toneAndCommunication?.selectedTones &&
                data.content.toneAndCommunication.selectedTones.length > 0
            ),
        },
        {
            label: "Select at least 1 principle for your interview flow",
            completed: !!(
                data.content.interviewFlowPrinciples?.selectedPrinciples &&
                data.content.interviewFlowPrinciples.selectedPrinciples.length > 0
            ),
        },
    ];

    const completedCount = checklistItems.filter((item) => item.completed).length;
    const totalCount = checklistItems.length;
    const progressPercentage = Math.round((completedCount / totalCount) * 100);

    // Validate if all required fields are filled
    const isValid =
        !!(
            data.content.interviewerPersona && data.content.interviewerPersona.trim()
        ) &&
        !!(
            data.content.toneAndCommunication?.selectedTones &&
            data.content.toneAndCommunication.selectedTones.length > 0
        ) &&
        !!(
            data.content.interviewFlowPrinciples?.selectedPrinciples &&
            data.content.interviewFlowPrinciples.selectedPrinciples.length > 0
        );

    // Notify parent of validation status
    if (onValidationChange) {
        onValidationChange(isValid);
    }

    const handleFieldChange = (
        field: string,
        value: string | object
    ) => {
        onDataChange({
            ...data,
            content: {
                ...data.content,
                [field]: value,
            },
        });
    };

    return (
        <div
            className="pb-8 border-b border-gray-200"
            style={{ paddingTop: "24px", paddingBottom: "40px" }}
        >
            {/* Two-Panel Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Panel: Header and Description */}
                <div className="lg:col-span-3 flex flex-col space-y-4">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3 pr-[16px]">
                            <div
                                className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30"
                                style={{ backgroundColor: "#dbeafe", color: "#2563eb" }}
                            >
                                <MessageSquare
                                    className="w-5 h-5 text-blue-600 dark:text-blue-400"
                                    style={{ color: "#2563eb" }}
                                />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-start justify-between gap-2">
                                    <h2 className="text-lg font-semibold">Interview Context</h2>
                                    <span
                                        className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded"
                                        style={{
                                            paddingTop: "4px",
                                            paddingBottom: "4px",
                                            borderRadius: "1rem",
                                        }}
                                    >
                                        Step 2
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Define how your AI interviewer should behave and communicate
                        throughout the interview.
                        <br />
                        <br />
                        Set the persona, tone, and flow principles to match your interview
                        style.
                    </p>

                    {/* Spacer to push progress checklist to bottom */}
                    <div className="flex-1" />

                    {/* Progress Checklist - Moved to bottom */}
                    <div className="mt-auto space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Complete your Interview Context
                            </h3>
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                                {completedCount}/{totalCount} completed
                            </span>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div
                                className="bg-blue-600 dark:bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        </div>

                        {/* Checklist Items */}
                        <div className="space-y-2.5 pt-1">
                            {checklistItems.map((item, index) => (
                                <div key={index} className="flex items-start gap-2.5">
                                    <div className="mt-0.5">
                                        {item.completed ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                        ) : (
                                            <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                        )}
                                    </div>
                                    <span
                                        className={`text-sm leading-tight ${item.completed ? "text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"}`}
                                    >
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Panel: Input Fields */}
                <div className="lg:col-span-9 space-y-6">
                    {/* 1. Interviewer Persona */}
                    <div className="space-y-2">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-600" />
                            <div>
                                Interviewer Persona
                                <span className="text-red-500">*</span>
                            </div>
                        </Label>
                        {mode === "edit" ? (
                            <>
                                <p className="text-xs text-gray-500">
                                    Describe the professional persona the AI should adopt, or what
                                    role they represent in the interview.
                                </p>
                                <Textarea
                                    value={data.content.interviewerPersona || ""}
                                    onChange={(e) =>
                                        handleFieldChange("interviewerPersona", e.target.value)
                                    }
                                    placeholder="Describe the AI's persona, for example: 'You are acting as a hiring manager evaluating communication, professionalism, and role fit.'"
                                    className="min-h-[80px] resize-y"
                                />
                            </>
                        ) : (
                            <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md min-h-[80px]">
                                <span
                                    className={
                                        data.content.interviewerPersona
                                            ? "text-gray-900"
                                            : "text-gray-500"
                                    }
                                    style={
                                        !data.content.interviewerPersona
                                            ? { color: "#9ca3af" }
                                            : undefined
                                    }
                                >
                                    {renderTextWithLineBreaks(data.content.interviewerPersona)}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* 2. Tone & Communication */}
                    <div className="space-y-3">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-blue-600" />
                            <div>
                                Tone & Communication
                                <span className="text-red-500">*</span>
                            </div>
                        </Label>
                        {mode === "edit" ? (
                            <>
                                <p className="text-xs text-gray-500">
                                    Select the tone and communication style(s) the AI should
                                    maintain. Choose one or more options that fit your interview
                                    style.
                                </p>
                                <div className="space-y-4">
                                    {/* Checkbox Options */}
                                    <div className="space-y-3">
                                        {defaultToneAndCommunication.map((tone) => (
                                            <div key={tone.id} className="flex items-start space-x-3">
                                                <Checkbox
                                                    id={tone.id}
                                                    checked={
                                                        data.content.toneAndCommunication?.selectedTones?.includes(
                                                            tone.id
                                                        ) || false
                                                    }
                                                    onCheckedChange={(checked) => {
                                                        const currentTones =
                                                            data.content.toneAndCommunication?.selectedTones || [];
                                                        const newTones = checked
                                                            ? [...currentTones, tone.id]
                                                            : currentTones.filter((t) => t !== tone.id);
                                                        handleFieldChange("toneAndCommunication", {
                                                            ...data.content.toneAndCommunication,
                                                            enabled: true,
                                                            selectedTones: newTones,
                                                        });
                                                    }}
                                                    className="mt-1"
                                                />

                                                <div className="flex-1">
                                                    <label
                                                        htmlFor={tone.id}
                                                        className="text-sm font-medium leading-none cursor-pointer"
                                                    >
                                                        {tone.label}
                                                    </label>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {tone.description}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Additional Preferences Textarea */}
                                    <div className="space-y-2">
                                        <Label className="text-sm font-medium">
                                            Additional Preferences
                                        </Label>
                                        <Textarea
                                            value={
                                                data.content.toneAndCommunication?.additionalPreferences || ""
                                            }
                                            onChange={(e) => {
                                                handleFieldChange("toneAndCommunication", {
                                                    ...data.content.toneAndCommunication,
                                                    additionalPreferences: e.target.value,
                                                });
                                            }}
                                            placeholder="Add any extra tone or communication preferences."
                                            className="min-h-[80px] resize-y"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-3">
                                {data.content.toneAndCommunication?.selectedTones &&
                                    data.content.toneAndCommunication.selectedTones.length > 0 ? (
                                    <>
                                        {/* Selected Tones Section - Chips Only */}
                                        <div>
                                            <div className="text-xs font-medium text-gray-500 mb-2">
                                                Selected Communication Styles:
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {data.content.toneAndCommunication.selectedTones.map(
                                                    (toneId) => {
                                                        if (defaultToneAndCommunication.map((t) => t.id).indexOf(toneId) === -1) return null;
                                                        return (
                                                            <span
                                                                key={toneId}
                                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                            >
                                                                {mapToneAndCommunication[toneId]}
                                                            </span>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                        {/* Additional Preferences Section */}
                                        {data.content.toneAndCommunication.additionalPreferences && (
                                            <div>
                                                <div className="text-xs font-medium text-gray-500 mb-2">
                                                    Additional Preferences:
                                                </div>
                                                <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md">
                                                    <p className="text-gray-900">
                                                        {data.content.toneAndCommunication.additionalPreferences}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-gray-500" style={{ color: "#9ca3af" }}>
                                        No tone selected. Please select at least one communication
                                        style.
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* 3. Interview Flow Principles */}
                    <div className="space-y-3">
                        <Label className="text-base font-medium flex items-center gap-2">
                            <GitBranch className="w-4 h-4 text-blue-600" />
                            <div>
                                Interview Flow Principles
                                <span className="text-red-500">*</span>
                            </div>
                        </Label>
                        {mode === "edit" && (
                            <p className="text-xs text-gray-500">
                                Select the evaluation and flow approach(es) the AI should use.
                                Choose one or more principles that align with your role
                                requirements.
                            </p>
                        )}
                        {mode === "edit" ? (
                            <div className="space-y-4">
                                {/* Checkbox Options */}
                                <div className="space-y-3">
                                    {defaultInterviewFlow.map((principle) => (
                                        <div
                                            key={principle.id}
                                            className="flex items-start space-x-3"
                                        >
                                            <Checkbox
                                                id={principle.id}
                                                checked={
                                                    data.content.interviewFlowPrinciples?.selectedPrinciples?.includes(
                                                        principle.id
                                                    ) || false
                                                }
                                                onCheckedChange={(checked) => {
                                                    const currentPrinciples =
                                                        data.content.interviewFlowPrinciples?.selectedPrinciples ||
                                                        [];
                                                    const newPrinciples = checked
                                                        ? [...currentPrinciples, principle.id]
                                                        : currentPrinciples.filter(
                                                            (p) => p !== principle.id
                                                        );

                                                    handleFieldChange("interviewFlowPrinciples", {
                                                        ...data.content.interviewFlowPrinciples,
                                                        enabled: true,
                                                        selectedPrinciples: newPrinciples,
                                                    });
                                                }}
                                                className="mt-1"
                                            />

                                            <div className="flex-1">
                                                <label
                                                    htmlFor={principle.id}
                                                    className="text-sm font-medium leading-none cursor-pointer"
                                                >
                                                    {principle.label}
                                                </label>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {principle.description}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Additional Guidelines Textarea */}
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium">
                                        Additional Guidelines
                                    </Label>
                                    <Textarea
                                        value={
                                            data.content.interviewFlowPrinciples?.additionalGuidelines || ""
                                        }
                                        onChange={(e) => {
                                            handleFieldChange("interviewFlowPrinciples", {
                                                ...data.content.interviewFlowPrinciples,
                                                additionalGuidelines: e.target.value,
                                            });
                                        }}
                                        placeholder="Add any additional flow or evaluation guidelines."
                                        className="min-h-[80px] resize-y"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {data.content.interviewFlowPrinciples?.selectedPrinciples &&
                                    data.content.interviewFlowPrinciples.selectedPrinciples.length > 0 ? (
                                    <>
                                        {/* Selected Principles Section - Chips Only */}
                                        <div>
                                            <div className="text-xs font-medium text-gray-500 mb-2">
                                                Selected Flow Principles:
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {data.content.interviewFlowPrinciples.selectedPrinciples.map(
                                                    (principleId) => {
                                                        if (defaultInterviewFlow.map((p) => p.id).indexOf(principleId) === -1) return null;
                                                        return (
                                                            <span
                                                                key={principleId}
                                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                            >
                                                                {mapInterviewFlow[principleId]}
                                                            </span>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                        {/* Additional Guidelines Section */}
                                        {data.content.interviewFlowPrinciples.additionalGuidelines && (
                                            <div>
                                                <div className="text-xs font-medium text-gray-500 mb-2">
                                                    Additional Guidelines:
                                                </div>
                                                <div className="text-sm py-2 px-3 bg-gray-50 border border-gray-200 rounded-md">
                                                    <p className="text-gray-900">
                                                        {data.content.interviewFlowPrinciples.additionalGuidelines}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-gray-500" style={{ color: "#9ca3af" }}>
                                        No principles selected. Please select at least one flow
                                        principle.
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
